
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { 
  $getSelection, 
  $isRangeSelection, 
  FORMAT_TEXT_COMMAND, 
  SELECTION_CHANGE_COMMAND,
  $createParagraphNode,
  $getNodeByKey
} from 'lexical';
import { $setBlocksType } from '@lexical/selection';
import { $createHeadingNode, $createQuoteNode } from '@lexical/rich-text';
import { INSERT_UNORDERED_LIST_COMMAND, INSERT_ORDERED_LIST_COMMAND } from '@lexical/list';
import { 
  INSERT_TABLE_COMMAND,
  $isTableNode,
  $getTableNodeFromLexicalNodeOrThrow,
  $insertTableRow__EXPERIMENTAL,
  $insertTableColumn__EXPERIMENTAL,
  $getTableRowIndexFromTableCellNode,
  $getTableColumnIndexFromTableCellNode,
  $isTableCellNode,
  $deleteTableRow__EXPERIMENTAL,
  $deleteTableColumn__EXPERIMENTAL
} from '@lexical/table';
import { useCallback, useEffect, useState } from 'react';

export default function AdvancedToolbarPlugin() {
  const [editor] = useLexicalComposerContext();
  const [isBold, setIsBold] = useState(false);
  const [isItalic, setIsItalic] = useState(false);
  const [isUnderline, setIsUnderline] = useState(false);
  const [isStrikethrough, setIsStrikethrough] = useState(false);
  const [isInTable, setIsInTable] = useState(false);
  const [showTableMenu, setShowTableMenu] = useState(false);
  const [showTableSizeSelector, setShowTableSizeSelector] = useState(false);
  const [showFontSizeMenu, setShowFontSizeMenu] = useState(false);
  const [showColorPicker, setShowColorPicker] = useState(false);
  const [selectedRows, setSelectedRows] = useState(3);
  const [selectedCols, setSelectedCols] = useState(3);
  const [currentFontSize, setCurrentFontSize] = useState('16px');
  const [currentColor, setCurrentColor] = useState('#000000');

  const updateToolbar = useCallback(() => {
    const selection = $getSelection();
    if ($isRangeSelection(selection)) {
      setIsBold(selection.hasFormat('bold'));
      setIsItalic(selection.hasFormat('italic'));
      setIsUnderline(selection.hasFormat('underline'));
      setIsStrikethrough(selection.hasFormat('strikethrough'));

      // 检查是否在表格中
      const anchorNode = selection.anchor.getNode();
      const element = anchorNode.getKey() === 'root' 
        ? anchorNode 
        : anchorNode.getTopLevelElementOrThrow();
      
      const tableNode = element.getParent();
      setIsInTable($isTableNode(tableNode));
    }
  }, []);

  useEffect(() => {
    return editor.registerCommand(
      SELECTION_CHANGE_COMMAND,
      () => {
        updateToolbar();
        return false;
      },
      1
    );
  }, [editor, updateToolbar]);

  const formatHeading = (headingSize: 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6') => {
    editor.update(() => {
      const selection = $getSelection();
      if ($isRangeSelection(selection)) {
        $setBlocksType(selection, () => $createHeadingNode(headingSize));
      }
    });
  };

  const formatParagraph = () => {
    editor.update(() => {
      const selection = $getSelection();
      if ($isRangeSelection(selection)) {
        $setBlocksType(selection, () => $createParagraphNode());
      }
    });
  };

  const formatQuote = () => {
    editor.update(() => {
      const selection = $getSelection();
      if ($isRangeSelection(selection)) {
        $setBlocksType(selection, () => $createQuoteNode());
      }
    });
  };

  const insertBulletList = () => {
    editor.dispatchCommand(INSERT_UNORDERED_LIST_COMMAND, undefined);
  };

  const insertNumberedList = () => {
    editor.dispatchCommand(INSERT_ORDERED_LIST_COMMAND, undefined);
  };

  const insertTable = (rows: number = 3, cols: number = 3) => {
    editor.dispatchCommand(INSERT_TABLE_COMMAND, { columns: cols.toString(), rows: rows.toString() });
    setShowTableMenu(false);
    setShowTableSizeSelector(false);
  };

  const insertTableRow = (insertAfter: boolean) => {
    editor.update(() => {
      const selection = $getSelection();
      if ($isRangeSelection(selection)) {
        const anchorNode = selection.anchor.getNode();
        const cellNode = anchorNode.getParent();
        
        if ($isTableCellNode(cellNode)) {
          const tableNode = $getTableNodeFromLexicalNodeOrThrow(cellNode);
          const rowIndex = $getTableRowIndexFromTableCellNode(cellNode);
          
          $insertTableRow__EXPERIMENTAL(
            tableNode,
            insertAfter ? rowIndex + 1 : rowIndex
          );
        }
      }
    });
    setShowTableMenu(false);
  };

  const insertTableColumn = (insertAfter: boolean) => {
    editor.update(() => {
      const selection = $getSelection();
      if ($isRangeSelection(selection)) {
        const anchorNode = selection.anchor.getNode();
        const cellNode = anchorNode.getParent();
        
        if ($isTableCellNode(cellNode)) {
          const tableNode = $getTableNodeFromLexicalNodeOrThrow(cellNode);
          const columnIndex = $getTableColumnIndexFromTableCellNode(cellNode);
          
          $insertTableColumn__EXPERIMENTAL(
            tableNode,
            insertAfter ? columnIndex + 1 : columnIndex
          );
        }
      }
    });
    setShowTableMenu(false);
  };

  const deleteTableRow = () => {
    editor.update(() => {
      const selection = $getSelection();
      if ($isRangeSelection(selection)) {
        const anchorNode = selection.anchor.getNode();
        const cellNode = anchorNode.getParent();
        
        if ($isTableCellNode(cellNode)) {
          const tableNode = $getTableNodeFromLexicalNodeOrThrow(cellNode);
          const rowIndex = $getTableRowIndexFromTableCellNode(cellNode);
          
          $deleteTableRow__EXPERIMENTAL(tableNode, rowIndex);
        }
      }
    });
    setShowTableMenu(false);
  };

  const deleteTableColumn = () => {
    editor.update(() => {
      const selection = $getSelection();
      if ($isRangeSelection(selection)) {
        const anchorNode = selection.anchor.getNode();
        const cellNode = anchorNode.getParent();
        
        if ($isTableCellNode(cellNode)) {
          const tableNode = $getTableNodeFromLexicalNodeOrThrow(cellNode);
          const columnIndex = $getTableColumnIndexFromTableCellNode(cellNode);
          
          $deleteTableColumn__EXPERIMENTAL(tableNode, columnIndex);
        }
      }
    });
    setShowTableMenu(false);
  };

  const applyFontSize = (fontSize: string) => {
    editor.update(() => {
      const selection = $getSelection();
      if ($isRangeSelection(selection)) {
        const nodes = selection.getNodes();
        nodes.forEach(node => {
          const element = node.getParent();
          if (element) {
            element.setStyle(`font-size: ${fontSize}`);
          }
        });
      }
    });
    setCurrentFontSize(fontSize);
    setShowFontSizeMenu(false);
  };

  const applyTextColor = (color: string) => {
    editor.update(() => {
      const selection = $getSelection();
      if ($isRangeSelection(selection)) {
        const nodes = selection.getNodes();
        nodes.forEach(node => {
          const element = node.getParent();
          if (element) {
            element.setStyle(`color: ${color}`);
          }
        });
      }
    });
    setCurrentColor(color);
    setShowColorPicker(false);
  };

  // 表格尺寸选择器组件
  const TableSizeSelector = () => {
    const maxRows = 10;
    const maxCols = 10;
    
    const handleCellHover = (row: number, col: number) => {
      setSelectedRows(row);
      setSelectedCols(col);
    };

    const handleCellClick = (row: number, col: number) => {
      insertTable(row, col);
    };

    return (
      <div className="p-4 bg-white">
        <div className="text-sm text-gray-700 mb-3 text-center">
          选择表格大小: {selectedRows} × {selectedCols}
        </div>
        <div className="grid grid-cols-10 gap-1 mb-3">
          {Array.from({ length: maxRows }, (_, rowIndex) => 
            Array.from({ length: maxCols }, (_, colIndex) => {
              const row = rowIndex + 1;
              const col = colIndex + 1;
              const isSelected = row <= selectedRows && col <= selectedCols;
              
              return (
                <div
                  key={`${row}-${col}`}
                  className={`w-4 h-4 border border-gray-300 cursor-pointer transition-colors ${
                    isSelected ? 'bg-blue-500' : 'bg-white hover:bg-blue-100'
                  }`}
                  onMouseEnter={() => handleCellHover(row, col)}
                  onClick={() => handleCellClick(row, col)}
                />
              );
            })
          )}
        </div>
        <div className="flex justify-between">
          <button
            onClick={() => setShowTableSizeSelector(false)}
            className="px-3 py-1 text-sm text-gray-600 hover:text-gray-800"
          >
            取消
          </button>
          <button
            onClick={() => insertTable(selectedRows, selectedCols)}
            className="px-3 py-1 text-sm bg-blue-600 text-white rounded hover:bg-blue-700"
          >
            插入表格
          </button>
        </div>
      </div>
    );
  };

  const fontSizes = ['12px', '14px', '16px', '18px', '20px', '24px', '28px', '32px', '36px', '48px'];
  const colors = [
    '#000000', '#333333', '#666666', '#999999', '#CCCCCC',
    '#FF0000', '#FF6600', '#FFCC00', '#00FF00', '#0066FF',
    '#6600FF', '#FF0066', '#00FFFF', '#FF00FF', '#800000',
    '#008000', '#000080', '#800080', '#008080', '#808000'
  ];

  return (
    <div className="flex items-center gap-1 p-3 border-b border-gray-200 bg-gray-50 rounded-t-lg flex-wrap">
      {/* 段落格式 */}
      <div className="flex items-center gap-1 mr-2">
        <button
          onClick={formatParagraph}
          className="px-3 py-1 text-sm font-medium text-gray-700 hover:bg-gray-200 rounded transition-colors"
          title="正文"
        >
          正文
        </button>
        <button
          onClick={() => formatHeading('h1')}
          className="px-3 py-1 text-sm font-medium text-gray-700 hover:bg-gray-200 rounded transition-colors"
          title="标题1"
        >
          H1
        </button>
        <button
          onClick={() => formatHeading('h2')}
          className="px-3 py-1 text-sm font-medium text-gray-700 hover:bg-gray-200 rounded transition-colors"
          title="标题2"
        >
          H2
        </button>
        <button
          onClick={() => formatHeading('h3')}
          className="px-3 py-1 text-sm font-medium text-gray-700 hover:bg-gray-200 rounded transition-colors"
          title="标题3"
        >
          H3
        </button>
      </div>

      <div className="w-px h-6 bg-gray-300 mx-1"></div>

      {/* 字体大小 */}
      <div className="relative">
        <button
          onClick={() => setShowFontSizeMenu(!showFontSizeMenu)}
          className="px-3 py-1 text-sm font-medium text-gray-700 hover:bg-gray-200 rounded transition-colors flex items-center"
          title="字体大小"
        >
          <i className="ri-font-size text-sm mr-1"></i>
          {currentFontSize}
          <i className="ri-arrow-down-s-line text-xs ml-1"></i>
        </button>
        
        {showFontSizeMenu && (
          <div className="absolute top-full left-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg z-10 min-w-[120px]">
            {fontSizes.map(size => (
              <button
                key={size}
                onClick={() => applyFontSize(size)}
                className={`w-full px-3 py-2 text-left text-sm hover:bg-gray-100 ${
                  currentFontSize === size ? 'bg-blue-50 text-blue-600' : 'text-gray-700'
                }`}
              >
                {size}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* 文字颜色 */}
      <div className="relative">
        <button
          onClick={() => setShowColorPicker(!showColorPicker)}
          className="w-8 h-8 flex items-center justify-center rounded transition-colors text-gray-700 hover:bg-gray-200 relative"
          title="文字颜色"
        >
          <i className="ri-font-color text-sm"></i>
          <div 
            className="absolute bottom-0 left-1/2 transform -translate-x-1/2 w-4 h-1 rounded"
            style={{ backgroundColor: currentColor }}
          ></div>
        </button>
        
        {showColorPicker && (
          <div className="absolute top-full left-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg z-10 p-3">
            <div className="grid grid-cols-5 gap-2 mb-3">
              {colors.map(color => (
                <button
                  key={color}
                  onClick={() => applyTextColor(color)}
                  className={`w-6 h-6 rounded border-2 transition-all ${
                    currentColor === color ? 'border-blue-500 scale-110' : 'border-gray-300 hover:border-gray-400'
                  }`}
                  style={{ backgroundColor: color }}
                  title={color}
                />
              ))}
            </div>
            <input
              type="color"
              value={currentColor}
              onChange={(e) => applyTextColor(e.target.value)}
              className="w-full h-8 rounded border border-gray-300"
            />
          </div>
        )}
      </div>

      <div className="w-px h-6 bg-gray-300 mx-1"></div>

      {/* 文本格式 */}
      <div className="flex items-center gap-1 mr-2">
        <button
          onClick={() => editor.dispatchCommand(FORMAT_TEXT_COMMAND, 'bold')}
          className={`w-8 h-8 flex items-center justify-center rounded transition-colors ${
            isBold ? 'bg-blue-100 text-blue-600' : 'text-gray-700 hover:bg-gray-200'
          }`}
          title="粗体"
        >
          <i className="ri-bold text-sm"></i>
        </button>
        <button
          onClick={() => editor.dispatchCommand(FORMAT_TEXT_COMMAND, 'italic')}
          className={`w-8 h-8 flex items-center justify-center rounded transition-colors ${
            isItalic ? 'bg-blue-100 text-blue-600' : 'text-gray-700 hover:bg-gray-200'
          }`}
          title="斜体"
        >
          <i className="ri-italic text-sm"></i>
        </button>
        <button
          onClick={() => editor.dispatchCommand(FORMAT_TEXT_COMMAND, 'underline')}
          className={`w-8 h-8 flex items-center justify-center rounded transition-colors ${
            isUnderline ? 'bg-blue-100 text-blue-600' : 'text-gray-700 hover:bg-gray-200'
          }`}
          title="下划线"
        >
          <i className="ri-underline text-sm"></i>
        </button>
        <button
          onClick={() => editor.dispatchCommand(FORMAT_TEXT_COMMAND, 'strikethrough')}
          className={`w-8 h-8 flex items-center justify-center rounded transition-colors ${
            isStrikethrough ? 'bg-blue-100 text-blue-600' : 'text-gray-700 hover:bg-gray-200'
          }`}
          title="删除线"
        >
          <i className="ri-strikethrough text-sm"></i>
        </button>
      </div>

      <div className="w-px h-6 bg-gray-300 mx-1"></div>

      {/* 列表 */}
      <div className="flex items-center gap-1 mr-2">
        <button
          onClick={insertBulletList}
          className="w-8 h-8 flex items-center justify-center text-gray-700 hover:bg-gray-200 rounded transition-colors"
          title="无序列表"
        >
          <i className="ri-list-unordered text-sm"></i>
        </button>
        <button
          onClick={insertNumberedList}
          className="w-8 h-8 flex items-center justify-center text-gray-700 hover:bg-gray-200 rounded transition-colors"
          title="有序列表"
        >
          <i className="ri-list-ordered text-sm"></i>
        </button>
      </div>

      <div className="w-px h-6 bg-gray-300 mx-1"></div>

      {/* 表格 */}
      <div className="relative">
        <button
          onClick={() => {
            if (isInTable) {
              setShowTableMenu(!showTableMenu);
              setShowTableSizeSelector(false);
            } else {
              setShowTableSizeSelector(!showTableSizeSelector);
              setShowTableMenu(false);
            }
          }}
          className="w-8 h-8 flex items-center justify-center text-gray-700 hover:bg-gray-200 rounded transition-colors"
          title="表格操作"
        >
          <i className="ri-table-line text-sm"></i>
        </button>
        
        {/* 表格尺寸选择器 */}
        {showTableSizeSelector && !isInTable && (
          <div className="absolute top-full left-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg z-10">
            <TableSizeSelector />
          </div>
        )}

        {/* 表格操作菜单 */}
        {showTableMenu && isInTable && (
          <div className="absolute top-full left-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg z-10 min-w-[200px]">
            <button
              onClick={() => {
                setShowTableMenu(false);
                setShowTableSizeSelector(true);
              }}
              className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-100 flex items-center"
            >
              <i className="ri-table-line mr-2"></i>
              插入新表格
            </button>
            
            <div className="border-t border-gray-200 my-1"></div>
            <button
              onClick={() => insertTableRow(false)}
              className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-100 flex items-center"
            >
              <i className="ri-insert-row-top mr-2"></i>
              在上方插入行
            </button>
            <button
              onClick={() => insertTableRow(true)}
              className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-100 flex items-center"
            >
              <i className="ri-insert-row-bottom mr-2"></i>
              在下方插入行
            </button>
            <button
              onClick={() => insertTableColumn(false)}
              className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-100 flex items-center"
            >
              <i className="ri-insert-column-left mr-2"></i>
              在左侧插入列
            </button>
            <button
              onClick={() => insertTableColumn(true)}
              className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-100 flex items-center"
            >
              <i className="ri-insert-column-right mr-2"></i>
              在右侧插入列
            </button>
            
            <div className="border-t border-gray-200 my-1"></div>
            <button
              onClick={deleteTableRow}
              className="w-full px-4 py-2 text-left text-sm text-red-600 hover:bg-red-50 flex items-center"
            >
              <i className="ri-delete-row mr-2"></i>
              删除当前行
            </button>
            <button
              onClick={deleteTableColumn}
              className="w-full px-4 py-2 text-left text-sm text-red-600 hover:bg-red-50 flex items-center"
            >
              <i className="ri-delete-column mr-2"></i>
              删除当前列
            </button>
          </div>
        )}
      </div>

      <div className="w-px h-6 bg-gray-300 mx-1"></div>

      {/* 引用 */}
      <button
        onClick={formatQuote}
        className="w-8 h-8 flex items-center justify-center text-gray-700 hover:bg-gray-200 rounded transition-colors"
        title="引用"
      >
        <i className="ri-double-quotes-l text-sm"></i>
      </button>
    </div>
  );
}
